// app/javascript/controllers/hotspot_editor_controller.js
import { Controller } from "@hotwired/stimulus"

// managers
import {
  initViewer,
  loadTextureFromUrl,
  computeLimitsDeg,
  setCenteredView,
  lonLatFromScreen,
  renderHotspots,
  updateHotspotPositions,
  startLoop,
  stopLoop,
  bindResize,
  unbindResize,
  disposeAll,
} from "../scenes_editor/hotspots_manager"

import {
  initTools,
  renderToolsSection,
} from "../scenes_editor/tools_manager"

import {
  initInteractions,
  ensureTmpInteractionsBucket,
  moveInteractionsBucket,
  deleteInteractionsBucket,
  renderInteractionsList,
  updateInteractionsUIState,
  loadInteractionsFromServer,
  addTextInteraction,
  addQuizInteraction,
  buildInteractionsAttributes,
} from "../scenes_editor/interactions_manager"

import {
  initWorkflowEditor,
  destroyWorkflowEditor,
  buildWorkflowPayload,
  saveWorkflow,
} from "../scenes_editor/workflow_manager"

const DEFAULT_LABEL = ""
const DEFAULT_ICON = ""
const DEFAULT_CSS = "hs-dot hs-dot--lg"
const DEFAULT_ACTIVE = true

export default class extends Controller {
  static targets = [
    "layer",
    "btnLeft",
    "btnRight",
    "btnCenter",
    "sidebar",

    // generale
    "inputName",
    "inputType",
    "inputLabel",
    "inputIcon",
    "inputCss",
    "inputActive",

    // step / pannelli
    "sectionGeneral",
    "sectionInteractions",
    "sectionTools",
    "panelGeneral",
    "panelInteractions",
    "panelTools",
    "chevGeneral",
    "chevInteractions",
    "chevTools",

    // tools
    "toolsList",

    // interazioni
    "interactionsList",
    "btnAddText",
    "btnAddQuiz",
    "interactionsHint",

    // insert mode + chips
    "btnInsert",
    "insertOverlay",
    "chipTotal",
    "chipActive",
    "chipInsert",

    "workflowFeedback",
    "workflowFeedbackInner",
    "workflowFeedbackText",
  ]

  static values = {
    projection: String,
    backgroundUrl: String,
    hotspots: Array,

    availableTools: Array,
    workflowSteps: Array,
    workflowUpdateUrl: String,

    createUrl: String,
    updateUrlTemplate: String,
    destroyUrlTemplate: String,
    csrfToken: String,

    interactionsIndexUrlTemplate: String,
  }

  connect() {
    this.viewerEl = this.element.querySelector("#pano-editor-viewer")
    if (!this.viewerEl) return

    if (!this.backgroundUrlValue) {
      console.warn("[EDITOR] backgroundUrlValue vuoto")
      return
    }

    this.initializeState()
    this.initializeManagers()
    this.initializeViewer()
    this.initializeUI()
    this.initializeEvents()

    this.hideSidebar()
    this.refreshChips()
    this.renderPanels()
    renderHotspots(this)
  }

  disconnect() {
    this.unbindEvents()

    stopLoop(this)
    unbindResize(this)
    this.unbindButtons?.()

    destroyWorkflowEditor(this)
    disposeAll(this)
  }

  initializeState() {
    this.openPanel = "general"

    this.hotspots = Array.isArray(this.hotspotsValue)
      ? [...this.hotspotsValue].filter((h) => h.is_active !== false)
      : []

    this.workflowSteps = Array.isArray(this.workflowStepsValue)
      ? [...this.workflowStepsValue]
      : []

    this.hotspotEls = new Map()
    this.originalById = new Map()

    this.selectedId = null
    this.editingId = null

    this.dragging = false
    this._dragPointerId = null
    this._dragFromToolbar = false

    this.insertMode = false

    this._onBgClick = null
    this._onKeyDown = null
    this._onPointerMove = null
    this._onPointerUp = null
  }

  initializeManagers() {
    initTools(this)
    initInteractions(this)
    initWorkflowEditor(this)
  }

  initializeViewer() {
    initViewer(this)
    loadTextureFromUrl(this, this.backgroundUrlValue)

    computeLimitsDeg(this)
    setCenteredView(this)

    startLoop(this)
    bindResize(this)
  }

  initializeUI() {
    this.bindButtons()

    if (this.hasBtnAddTextTarget) {
      this._onAddTextClick = (e) => addTextInteraction(this, e)
      this.btnAddTextTarget.addEventListener("click", this._onAddTextClick)
    }

    if (this.hasBtnAddQuizTarget) {
      this._onAddQuizClick = (e) => addQuizInteraction(this, e)
      this.btnAddQuizTarget.addEventListener("click", this._onAddQuizClick)
    }
  }

  initializeEvents() {
    this._onBgClick = (e) => {
      if (this.dragging) return

      if (this.insertMode) {
        this.createHotspotAtPointer(e)
        return
      }

      this.selectHotspot(null)
    }

    this._onKeyDown = (e) => {
      if (e.key === "Escape" && this.insertMode) {
        this.setInsertMode(false)
      }
    }

    this._onPointerMove = this.onPointerMove.bind(this)
    this._onPointerUp = this.onPointerUp.bind(this)

    this.viewerEl.addEventListener("click", this._onBgClick)
    window.addEventListener("keydown", this._onKeyDown)
    window.addEventListener("pointermove", this._onPointerMove)
    window.addEventListener("pointerup", this._onPointerUp)
    window.addEventListener("pointercancel", this._onPointerUp)
  }

  unbindEvents() {
    this.viewerEl?.removeEventListener("click", this._onBgClick)
    window.removeEventListener("keydown", this._onKeyDown)
    window.removeEventListener("pointermove", this._onPointerMove)
    window.removeEventListener("pointerup", this._onPointerUp)
    window.removeEventListener("pointercancel", this._onPointerUp)

    if (this.hasBtnAddTextTarget && this._onAddTextClick) {
      this.btnAddTextTarget.removeEventListener("click", this._onAddTextClick)
    }

    if (this.hasBtnAddQuizTarget && this._onAddQuizClick) {
      this.btnAddQuizTarget.removeEventListener("click", this._onAddQuizClick)
    }

    this._onBgClick = null
    this._onKeyDown = null
    this._onPointerMove = null
    this._onPointerUp = null
    this._onAddTextClick = null
    this._onAddQuizClick = null
  }

  /* =========================
   * CHIPS
   * ========================= */
  refreshChips() {
    const total = Array.isArray(this.hotspots) ? this.hotspots.length : 0
    const active = Array.isArray(this.hotspots)
      ? this.hotspots.filter((h) => h.is_active !== false).length
      : 0

    if (this.hasChipTotalTarget) this.chipTotalTarget.textContent = String(total)
    if (this.hasChipActiveTarget) this.chipActiveTarget.textContent = String(active)
  }

  /* =========================
   * INSERT MODE
   * ========================= */
  toggleInsertMode(e) {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    this.setInsertMode(!this.insertMode)
  }

  setInsertMode(on) {
    this.insertMode = !!on

    if (this.hasInsertOverlayTarget) {
      this.insertOverlayTarget.classList.toggle("hidden", !this.insertMode)
    }

    if (this.hasChipInsertTarget) {
      this.chipInsertTarget.classList.toggle("hidden", !this.insertMode)
    }

    if (this.hasBtnInsertTarget) {
      if (this.insertMode) {
        this.btnInsertTarget.classList.remove("bg-blue-600", "hover:bg-blue-700")
        this.btnInsertTarget.classList.add("bg-gray-800", "hover:bg-gray-900")
        this.btnInsertTarget.textContent = "✕ Annulla inserimento"
      } else {
        this.btnInsertTarget.classList.remove("bg-gray-800", "hover:bg-gray-900")
        this.btnInsertTarget.classList.add("bg-blue-600", "hover:bg-blue-700")
        this.btnInsertTarget.textContent = "➕ Inserisci nuovo hotspot"
      }
    }

    if (this.viewerEl) {
      this.viewerEl.style.cursor = this.insertMode ? "crosshair" : ""
    }
  }

  addHotspot(e) {
    this.toggleInsertMode(e)
  }

  createHotspotAtPointer(e) {
    const pos = lonLatFromScreen(this, e.clientX, e.clientY)
    const lon = pos?.lon ?? 0
    const lat = pos?.lat ?? 0

    this.addHotspotAt(lon, lat)
    this.setInsertMode(false)
  }

  addHotspotAt(lon, lat) {
    const tmpId = `tmp_${Date.now()}`

    const hs = {
      id: tmpId,
      name: "",
      label: DEFAULT_LABEL,
      hotspot_type: "Testo",
      icon: DEFAULT_ICON,
      css_classes: DEFAULT_CSS,
      lon,
      lat,
      is_active: DEFAULT_ACTIVE,
      _isNew: true,
    }

    this.hotspots.push(hs)

    ensureTmpInteractionsBucket(this, tmpId)

    renderHotspots(this)
    this.refreshChips()

    this.enableEditing(tmpId)
    this.selectHotspot(tmpId)

    this.dragging = false
    this._dragPointerId = null
    this._dragFromToolbar = false
  }

  /* =========================
   * ACCORDION
   * ========================= */
  togglePanel(e) {
    const panel = e?.params?.panel
    if (!panel) return

    this.openPanel = this.openPanel === panel ? null : panel
    this.renderPanels()
  }

  renderPanels() {
    const isGeneral = this.openPanel === "general"
    const isInteractions = this.openPanel === "interactions"
    const isTools = this.openPanel === "tools"

    if (this.hasPanelGeneralTarget) this.panelGeneralTarget.hidden = !isGeneral
    if (this.hasPanelInteractionsTarget) this.panelInteractionsTarget.hidden = !isInteractions
    if (this.hasPanelToolsTarget) this.panelToolsTarget.hidden = !isTools

    if (this.hasSectionGeneralTarget) this.sectionGeneralTarget.classList.toggle("is-open", isGeneral)
    if (this.hasSectionInteractionsTarget) this.sectionInteractionsTarget.classList.toggle("is-open", isInteractions)
    if (this.hasSectionToolsTarget) this.sectionToolsTarget.classList.toggle("is-open", isTools)

    if (this.hasChevGeneralTarget) this.chevGeneralTarget.textContent = isGeneral ? "▴" : "▾"
    if (this.hasChevInteractionsTarget) this.chevInteractionsTarget.textContent = isInteractions ? "▴" : "▾"
    if (this.hasChevToolsTarget) this.chevToolsTarget.textContent = isTools ? "▴" : "▾"
  }

  /* =========================
   * SIDEBAR
   * ========================= */
  showSidebarFor(hs) {
    if (!this.hasSidebarTarget || !hs) return

    this.openPanel = "general"
    this.renderPanels()

    if (this.hasInputNameTarget) this.inputNameTarget.value = hs.name || ""
    if (this.hasInputTypeTarget) this.inputTypeTarget.value = hs.hotspot_type || "Testo"
    if (this.hasInputLabelTarget) this.inputLabelTarget.value = hs.label || DEFAULT_LABEL
    if (this.hasInputIconTarget) this.inputIconTarget.value = hs.icon || DEFAULT_ICON
    if (this.hasInputCssTarget) this.inputCssTarget.value = hs.css_classes || DEFAULT_CSS
    if (this.hasInputActiveTarget) this.inputActiveTarget.checked = hs.is_active !== false

    renderToolsSection(this, hs)
    renderInteractionsList(this, hs.id)
    updateInteractionsUIState(this, hs)

    this.sidebarTarget.hidden = false

    if (!String(hs.id).startsWith("tmp_")) {
      loadInteractionsFromServer(this, hs.id)
    }
  }

  hideSidebar() {
    if (!this.hasSidebarTarget) return
    this.openPanel = "general"
    this.sidebarTarget.hidden = true
  }

  /* =========================
   * HOTSPOT SELECTION
   * ========================= */
  renderHotspots() {
    renderHotspots(this)
  }

  selectHotspot(id) {
    this.selectedId = id

    if (id === null) {
      this.editingId = null
      this.hideSidebar()
    }

    for (const [hsId, el] of this.hotspotEls.entries()) {
      const isSelected = id !== null && hsId === id
      const isEditing = this.editingId === hsId

      el.classList.toggle("is-active", isSelected)
      el.classList.toggle("is-editing", isEditing)

      const existingTools = el.querySelector(".hs-dot__tools")
      if (existingTools) existingTools.remove()

      if (isSelected) {
        el.appendChild(this.createToolsBar(hsId))
      }
    }

    if (id !== null) {
      const hs = this.hotspots.find((h) => String(h.id) === String(id))
      if (hs) {
        renderToolsSection(this, hs)
        renderInteractionsList(this, hs.id)
      }
    }
  }

  createToolsBar(hsId) {
    const tools = document.createElement("div")
    tools.className = "hs-dot__tools"

    const isEditing = this.editingId === hsId

    const btnEditSave = document.createElement("div")
    btnEditSave.className = "hs-dot__btn"
    btnEditSave.textContent = isEditing ? "💾" : "✎"

    btnEditSave.addEventListener("pointerdown", (e) => {
      e.preventDefault()
      e.stopPropagation()
    })

    btnEditSave.addEventListener("click", (e) => {
      e.preventDefault()
      e.stopPropagation()

      if (this.editingId === hsId) {
        this.saveHotspot(hsId)
      } else {
        this.enableEditing(hsId)
      }
    })

    tools.appendChild(btnEditSave)

    if (!isEditing) {
      const btnDelete = document.createElement("div")
      btnDelete.className = "hs-dot__btn hs-dot__btn--danger"
      btnDelete.textContent = "✕"

      btnDelete.addEventListener("pointerdown", (e) => {
        e.preventDefault()
        e.stopPropagation()
      })

      btnDelete.addEventListener("click", (e) => {
        e.preventDefault()
        e.stopPropagation()
        this.deleteHotspot(hsId)
      })

      tools.appendChild(btnDelete)
    } else {
      const btnCancel = document.createElement("div")
      btnCancel.className = "hs-dot__btn"
      btnCancel.textContent = "↩"

      btnCancel.addEventListener("pointerdown", (e) => {
        e.preventDefault()
        e.stopPropagation()
      })

      btnCancel.addEventListener("click", (e) => {
        e.preventDefault()
        e.stopPropagation()
        this.cancelEditing(hsId)
      })

      tools.appendChild(btnCancel)
    }

    return tools
  }

  enableEditing(hsId) {
    const hs = this.hotspots.find((h) => String(h.id) === String(hsId))
    if (!hs) return

    this.originalById.set(hsId, {
      lon: hs.lon,
      lat: hs.lat,
      name: hs.name,
      label: hs.label,
      hotspot_type: hs.hotspot_type,
      icon: hs.icon,
      css_classes: hs.css_classes,
      is_active: hs.is_active,
    })

    this.editingId = hsId
    this.showSidebarFor(hs)
    this.selectHotspot(hsId)
  }

  cancelEditing(hsId) {
    const hs = this.hotspots.find((h) => String(h.id) === String(hsId))
    const orig = this.originalById.get(hsId)

    if (hs && orig) {
      hs.lon = orig.lon
      hs.lat = orig.lat
      hs.name = orig.name
      hs.label = orig.label
      hs.hotspot_type = orig.hotspot_type
      hs.icon = orig.icon
      hs.css_classes = orig.css_classes
      hs.is_active = orig.is_active

      updateHotspotPositions(this)
    }

    this.hideSidebar()
    this.editingId = null
    this.selectHotspot(hsId)
  }

  /* =========================
   * SAVE / CREATE / DELETE
   * ========================= */
  async saveHotspot(hsId) {
    const hs = this.hotspots.find((h) => String(h.id) === String(hsId))
    if (!hs) return

    hs.name = this.hasInputNameTarget ? this.inputNameTarget.value : ""
    hs.hotspot_type = this.hasInputTypeTarget ? this.inputTypeTarget.value : "Testo"

    hs.label = this.hasInputLabelTarget ? this.inputLabelTarget.value : DEFAULT_LABEL
    hs.icon = this.hasInputIconTarget ? this.inputIconTarget.value : DEFAULT_ICON
    hs.css_classes = this.hasInputCssTarget ? this.inputCssTarget.value : DEFAULT_CSS
    hs.is_active = this.hasInputActiveTarget ? this.inputActiveTarget.checked : DEFAULT_ACTIVE

    const selectedSet = this.selectedToolIdsByHotspotId?.get(hs.id) || new Set()
    hs._tool_ids = Array.from(selectedSet)

    const oldId = hs.id

    try {
      if (String(hsId).startsWith("tmp_")) {
        await this.createHotspotOnServer(hsId)
      } else {
        await this.updateHotspotOnServer(hsId)
      }
    } catch (err) {
      console.error("[EDITOR] save error", err)
      return
    }

    const persistedId = hs.id
    const hsNow = this.hotspots.find((h) => String(h.id) === String(persistedId))
    if (!hsNow) return

    this.selectedId = hsNow.id
    this.editingId = hsNow.id

    if (this.hasInputNameTarget) this.inputNameTarget.value = hsNow.name || ""

    renderToolsSection(this, hsNow)

    updateInteractionsUIState(this, hsNow)
    renderInteractionsList(this, hsNow.id)

    this.sidebarTarget.hidden = false
    this.openPanel = "interactions"
    this.renderPanels()

    this.selectHotspot(hsNow.id)

    if (!String(hsNow.id).startsWith("tmp_")) {
      await loadInteractionsFromServer(this, hsNow.id)
    }

    this.refreshWorkflowHotspotFlowRow?.(hsNow.id, oldId)
    this.refreshChips()
  }

  async updateHotspotOnServer(hsId) {
    const hs = this.hotspots.find((h) => String(h.id) === String(hsId))
    if (!hs) return

    const url = this.updateUrlTemplateValue.replace(":id", hsId)

    const resp = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfTokenValue,
        Accept: "application/json",
      },
      body: JSON.stringify({
        hotspot: {
          lon: hs.lon,
          lat: hs.lat,
          name: hs.name,
          label: hs.label,
          hotspot_type: hs.hotspot_type,
          icon: hs.icon,
          css_classes: hs.css_classes,
          is_active: hs.is_active,
          pulse_tool_ids: hs._tool_ids || [],
          interactions_attributes: buildInteractionsAttributes(this, hs.id),
        },
      }),
    })

    if (!resp.ok) {
      const txt = await resp.text()
      console.error("[EDITOR] update failed", resp.status, txt)
      throw new Error("update failed")
    }
  }

  async createHotspotOnServer(tmpId) {
    const hs = this.hotspots.find((h) => String(h.id) === String(tmpId))
    if (!hs) return

    const resp = await fetch(this.createUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfTokenValue,
        Accept: "application/json",
      },
      body: JSON.stringify({
        hotspot: {
          lon: hs.lon,
          lat: hs.lat,
          name: hs.name,
          label: hs.label,
          hotspot_type: hs.hotspot_type,
          icon: hs.icon,
          css_classes: hs.css_classes,
          is_active: hs.is_active,
          pulse_tool_ids: hs._tool_ids || [],
          interactions_attributes: buildInteractionsAttributes(this, tmpId),
        },
      }),
    })

    if (!resp.ok) {
      const txt = await resp.text()
      console.error("[EDITOR] create failed", resp.status, txt)
      throw new Error("create failed")
    }

    const data = await resp.json().catch(() => null)

    const newId =
      data?.id ||
      data?.hotspot?.id ||
      data?.data?.id ||
      data?.resource?.id

    if (!newId) {
      console.error("[EDITOR] create succeeded but no id found in response", data)
      throw new Error("create succeeded but no id returned")
    }

    const oldId = hs.id

    if (this.selectedToolIdsByHotspotId?.has(oldId)) {
      const set = this.selectedToolIdsByHotspotId.get(oldId)
      this.selectedToolIdsByHotspotId.delete(oldId)
      this.selectedToolIdsByHotspotId.set(newId, set)
    }

    moveInteractionsBucket(this, oldId, newId)

    if (this.originalById.has(oldId)) {
      const orig = this.originalById.get(oldId)
      this.originalById.delete(oldId)
      this.originalById.set(newId, orig)
    }

    hs.id = newId

    if (String(this.selectedId) === String(oldId)) this.selectedId = newId
    if (String(this.editingId) === String(oldId)) this.editingId = newId

    renderHotspots(this)

    const hsNow = this.hotspots.find((h) => String(h.id) === String(newId))
    if (hsNow) {
      updateInteractionsUIState(this, hsNow)
      renderInteractionsList(this, hsNow.id)
    }

    this.ensureWorkflowGroupsForHotspot(newId)

    this.refreshChips()
  }

  async deleteHotspot(hsId) {
    const targetId = String(hsId)

    // BLOCCO SE USATO NEL WORKFLOW
    if (this.isHotspotUsedInWorkflow(targetId)) {
      this.showHotspotInlineMessage(
        hsId,
        "Rimuovi prima questo hotspot dal workflow operativo.",
        "warning"
      )
      return
    }

    // -------------------------
    // HOTSPOT TEMPORANEO
    // -------------------------
    if (targetId.startsWith("tmp_")) {
      this.hotspots = this.hotspots.filter((h) => String(h.id) !== targetId)
      this.selectedToolIdsByHotspotId?.delete(targetId)
      deleteInteractionsBucket(this, targetId)

      // QUI è corretto NON toccare il workflow
      // (perché tmp non dovrebbe mai essere usato)

      if (String(this.editingId) === targetId) {
        this.editingId = null
        this.hideSidebar()
      }

      if (String(this.selectedId) === targetId) {
        this.selectedId = null
      }

      this.selectHotspot(null)
      renderHotspots(this)
      this.refreshChips()
      return
    }

    // -------------------------
    // HOTSPOT PERSISTITO
    // -------------------------
    if (!confirm("Sei sicuro di voler eliminare questo hotspot?")) return

    const url = this.destroyUrlTemplateValue.replace(":id", targetId)

    const resp = await fetch(url, {
      method: "DELETE",
      headers: {
        "X-CSRF-Token": this.csrfTokenValue,
        Accept: "application/json",
      },
    })

    if (!resp.ok) {
      const txt = await resp.text()
      console.error("[EDITOR] delete failed", resp.status, txt)
      return
    }

    // rimuovo dallo stato
    this.hotspots = this.hotspots.filter((h) => String(h.id) !== targetId)
    this.selectedToolIdsByHotspotId?.delete(targetId)
    deleteInteractionsBucket(this, targetId)

    // QUI è corretto rimuovere dal workflow
    // perché siamo sicuri che NON è usato
   
    this.removeWorkflowGroupsForHotspot(targetId)

    if (String(this.editingId) === targetId) {
      this.editingId = null
      this.hideSidebar()
    }

    if (String(this.selectedId) === targetId) {
      this.selectedId = null
    }

    this.selectHotspot(null)
    renderHotspots(this)
    this.refreshChips()
  }

  /* =========================
   * DRAG HOTSPOT
   * ========================= */
  onPointerMove(e) {
    if (!this.dragging || !this.editingId) return

    const pos = lonLatFromScreen(this, e.clientX, e.clientY)
    if (!pos) return

    const hs = this.hotspots.find((h) => String(h.id) === String(this.editingId))
    if (!hs) return

    hs.lon = pos.lon
    hs.lat = pos.lat

    updateHotspotPositions(this)
  }

  onPointerUp() {
    if (!this.dragging) return

    this.dragging = false

    const el = this.hotspotEls.get(this.editingId)
    if (el && this._dragPointerId != null) {
      try {
        el.releasePointerCapture?.(this._dragPointerId)
      } catch (_) {}
    }

    this._dragPointerId = null
    this._dragFromToolbar = false
  }

  /* =========================
   * BUTTONS VIEWER
   * ========================= */
  bindButtons() {
    this._unbindHoldLeft = this.bindHoldButton(
      this.hasBtnLeftTarget ? this.btnLeftTarget : null,
      "left"
    )

    this._unbindHoldRight = this.bindHoldButton(
      this.hasBtnRightTarget ? this.btnRightTarget : null,
      "right"
    )

    this._onCenter = (e) => {
      e.preventDefault()
      setCenteredView(this)
    }

    if (this.hasBtnCenterTarget) {
      this.btnCenterTarget.addEventListener("pointerdown", this._onCenter)
    }
  }

  unbindButtons() {
    this._unbindHoldLeft?.()
    this._unbindHoldRight?.()

    if (this._onCenter && this.hasBtnCenterTarget) {
      this.btnCenterTarget.removeEventListener("pointerdown", this._onCenter)
    }

    this._onCenter = null
  }

  bindHoldButton(btn, dir) {
    if (!btn) return () => {}

    const set = (value) => {
      if (dir === "left") this.uiLeft = value
      if (dir === "right") this.uiRight = value
      this.updateButtons()
    }

    const down = (e) => {
      e.preventDefault()
      btn.setPointerCapture?.(e.pointerId)
      set(true)
    }

    const release = (e) => {
      try {
        btn.releasePointerCapture?.(e.pointerId)
      } catch (_) {}
      set(false)
    }

    const leave = () => set(false)

    btn.addEventListener("pointerdown", down)
    btn.addEventListener("pointerup", release)
    btn.addEventListener("pointercancel", release)
    btn.addEventListener("pointerleave", leave)

    return () => {
      btn.removeEventListener("pointerdown", down)
      btn.removeEventListener("pointerup", release)
      btn.removeEventListener("pointercancel", release)
      btn.removeEventListener("pointerleave", leave)
    }
  }

  updateButtons() {
    if (this.hasBtnLeftTarget) {
      this.btnLeftTarget.classList.toggle("active", !!this.uiLeft)
    }

    if (this.hasBtnRightTarget) {
      this.btnRightTarget.classList.toggle("active", !!this.uiRight)
    }

    const isCentered = Math.abs(this.lon) < 0.1 && Math.abs(this.lat) < 0.1

    if (this.hasBtnCenterTarget) {
      this.btnCenterTarget.classList.toggle("active", !isCentered)
    }
  }

  ensureWorkflowGroupsForHotspot(hotspotId) {
    const hs = this.hotspots.find((h) => String(h.id) === String(hotspotId))
    if (!hs) return

    const availableList = this.element.querySelector("[data-workflow-available-list]")
    const usedList = this.element.querySelector("[data-workflow-used-list]")

    if (!availableList || !usedList) return

    const existingAvailable = availableList.querySelector(
      `[data-workflow-item-group][data-workflow-kind="hotspot_flow"][data-hotspot-id="${hs.id}"]`
    )

    const existingUsed = usedList.querySelector(
      `[data-workflow-item-group][data-workflow-kind="hotspot_flow"][data-hotspot-id="${hs.id}"]`
    )

    if (existingAvailable && existingUsed) return

    const interactions =
      this.interactionsByHotspotId?.get?.(hs.id) || []

    const activeInteractions = interactions
      .filter((it) => it && it.is_active !== false)
      .sort((a, b) => (a.position || 0) - (b.position || 0))

    const detailsText = activeInteractions.length
      ? activeInteractions.map((it) => `#${it.position} ${it.kind}`).join(" · ")
      : ""

    if (!existingAvailable) {
      const tbody = document.createElement("tbody")
      tbody.dataset.workflowItemGroup = ""
      tbody.dataset.workflowKind = "hotspot_flow"
      tbody.dataset.hotspotId = String(hs.id)
      tbody.dataset.workflowSeverityValue = "optional"
      tbody.dataset.workflowMessageValue = ""

      tbody.innerHTML = `
        <tr class="text-gray-800 bg-white border-b">
          <td class="py-3 px-3 align-top">
            <input type="checkbox"
                  class="rounded border-gray-300"
                  data-action="change->hotspot-editor#workflowToggleUse">
          </td>

          <td class="py-3 px-3 align-top">
            <span class="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold">
              Hotspot flow
            </span>
          </td>

          <td class="py-3 px-3 align-top font-medium" data-workflow-object>${hs.name || ""}</td>

          <td class="py-3 px-3 align-top" data-workflow-destination>
            <span class="text-gray-700">${activeInteractions.length} interazioni</span>
          </td>

          <td class="py-3 px-3 align-top text-gray-600" data-workflow-details>
            Apertura hotspot e completamento di tutte le interazioni collegate.
            ${detailsText ? `<div class="mt-1 text-xs text-gray-500">${detailsText}</div>` : ""}
          </td>
        </tr>
      `

      availableList.appendChild(tbody)
    }

    if (!existingUsed) {
      const wrapper = document.createElement("div")
      wrapper.dataset.workflowItemGroup = ""
      wrapper.dataset.workflowKind = "hotspot_flow"
      wrapper.dataset.hotspotId = String(hs.id)
      wrapper.dataset.workflowSeverityValue = "optional"
      wrapper.dataset.workflowMessageValue = ""
      wrapper.dataset.workflowPositionValue = "9999"
      wrapper.className = "hidden border-b bg-white"

      wrapper.innerHTML = `
        <div class="grid grid-cols-[48px_48px_64px_140px_minmax(180px,1fr)_minmax(160px,1fr)_176px] items-start px-3 pt-3 pb-2 text-sm text-gray-800">
          <div>
            <button type="button"
                    class="workflow-drag-handle text-gray-400 hover:text-gray-700 cursor-grab active:cursor-grabbing"
                    title="Trascina per riordinare">
              ⋮⋮
            </button>
          </div>

          <div class="font-semibold text-gray-400" data-workflow-order>1</div>

          <div>
            <input type="checkbox"
                  class="rounded border-gray-300"
                  data-action="change->hotspot-editor#workflowToggleUse">
          </div>

          <div>
            <span class="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold">
              Hotspot flow
            </span>
          </div>

          <div class="font-medium" data-workflow-object>${hs.name || ""}</div>

          <div class="text-gray-600" data-workflow-destination>${activeInteractions.length} interazioni</div>

          <div>
            <select class="w-40 rounded-lg border-gray-300 text-sm"
                    data-action="change->hotspot-editor#workflowFieldChanged"
                    data-workflow-severity>
              <option value="required">required</option>
              <option value="warning">warning</option>
              <option value="optional" selected>optional</option>
            </select>
          </div>
        </div>

        <div class="bg-gray-50/70 px-3 pb-4 pt-1">
          <div class="pl-1">
            <label class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
              Messaggio
            </label>
            <input type="text"
                  class="w-full rounded-lg border-gray-300"
                  placeholder="Messaggio opzionale"
                  value=""
                  data-action="input->hotspot-editor#workflowFieldChanged"
                  data-workflow-message>
          </div>
        </div>
      `

      const emptyBox = usedList.querySelector("[data-workflow-empty-used]")
      if (emptyBox) usedList.insertBefore(wrapper, emptyBox)
      else usedList.appendChild(wrapper)
    }

    destroyWorkflowEditor(this)
    initWorkflowEditor(this)
    this.refreshWorkflowHotspotFlowRow?.(hs.id)
  }

  removeWorkflowGroupsForHotspot(hotspotId) {
    const availableList = this.element.querySelector("[data-workflow-available-list]")
    if (!availableList) return

    const selector =
      `tbody[data-workflow-item-group][data-workflow-kind="hotspot_flow"][data-hotspot-id="${hotspotId}"]`

    availableList.querySelectorAll(selector).forEach((node) => node.remove())

    destroyWorkflowEditor(this)
    initWorkflowEditor(this)
  }

  isHotspotUsedInWorkflow(hotspotId) {
    const targetId = String(hotspotId)

    // 1. controllo DOM visibile nei workflow usati
    const usedList = this.element.querySelector("[data-workflow-used-list]")
    if (usedList) {
      const group = usedList.querySelector(
        `[data-workflow-item-group][data-workflow-kind="hotspot_flow"][data-hotspot-id="${targetId}"]`
      )

      if (
        group &&
        !group.hasAttribute("hidden") &&
        !group.classList.contains("hidden") &&
        group.style.display !== "none"
      ) {
        return true
      }
    }

    // 2. fallback: controllo i dati iniziali / correnti workflow
    const steps = Array.isArray(this.workflowSteps) ? this.workflowSteps : []

    return steps.some((step) => {
      const kind = step.kind || step["kind"]
      const hsId = step.hotspot_id || step["hotspot_id"]
      return kind === "hotspot_flow" && String(hsId) === targetId
    })
  }

  showHotspotInlineMessage(hsId, message, type = "warning") {
    const hotspotEl = this.hotspotEls?.get?.(hsId)
    if (!hotspotEl || !this.hasLayerTarget) return

    const oldMsg = hotspotEl.querySelector(".hs-inline-message")
    if (oldMsg) oldMsg.remove()

    const bubble = document.createElement("div")
    bubble.className = "hs-inline-message"
    bubble.textContent = message

    const isError = type === "error"
    const isWarning = type === "warning"

    bubble.style.position = "absolute"
    bubble.style.left = "50%"
    bubble.style.top = "calc(100% + 10px)"
    bubble.style.transform = "translateX(-50%)"
    bubble.style.minWidth = "220px"
    bubble.style.maxWidth = "320px"
    bubble.style.padding = "10px 12px"
    bubble.style.borderRadius = "12px"
    bubble.style.fontSize = "12px"
    bubble.style.lineHeight = "1.35"
    bubble.style.fontWeight = "600"
    bubble.style.textAlign = "center"
    bubble.style.whiteSpace = "normal"
    bubble.style.pointerEvents = "none"
    bubble.style.zIndex = "80"
    bubble.style.boxShadow = "0 12px 30px rgba(0,0,0,.22)"
    bubble.style.backdropFilter = "blur(6px)"
    bubble.style.webkitBackdropFilter = "blur(6px)"
    bubble.style.opacity = "0"
    bubble.style.transition = "opacity 180ms ease"

    if (isError) {
      bubble.style.color = "#fff"
      bubble.style.background = "rgba(153,27,27,.92)"
      bubble.style.border = "1px solid rgba(254,202,202,.55)"
    } else if (isWarning) {
      bubble.style.color = "#fff"
      bubble.style.background = "rgba(146,64,14,.92)"
      bubble.style.border = "1px solid rgba(253,230,138,.55)"
    } else {
      bubble.style.color = "#fff"
      bubble.style.background = "rgba(17,24,39,.88)"
      bubble.style.border = "1px solid rgba(255,255,255,.18)"
    }

    const arrow = document.createElement("div")
    arrow.style.position = "absolute"
    arrow.style.left = "50%"
    arrow.style.top = "-7px"
    arrow.style.width = "12px"
    arrow.style.height = "12px"
    arrow.style.transform = "translateX(-50%) rotate(45deg)"
    arrow.style.background = bubble.style.background
    arrow.style.borderLeft = bubble.style.border
    arrow.style.borderTop = bubble.style.border
    arrow.style.borderRight = "none"
    arrow.style.borderBottom = "none"

    bubble.appendChild(arrow)
    hotspotEl.appendChild(bubble)

    requestAnimationFrame(() => {
      bubble.style.opacity = "1"
    })

    clearTimeout(hotspotEl._inlineMessageTimer)
    hotspotEl._inlineMessageTimer = setTimeout(() => {
      bubble.style.opacity = "0"
      setTimeout(() => bubble.remove(), 200)
    }, 2800)
  }

  /* =========================
   * WORKFLOW
   * ========================= */
  workflowToggleUse() {
    // il comportamento vero è gestito nel workflow_manager
  }

  workflowFieldChanged() {
    // sync gestita nel workflow_manager
  }

  async saveWorkflow() {
    const result = await saveWorkflow(this)

    if (result.ok) {
      this.showWorkflowFeedback("✔ Salvato", "success")
      return
    }

    this.showWorkflowFeedback(result.error || "⚠ Errore", "error")
  }

  debugWorkflowPayload() {
    const payload = buildWorkflowPayload(this)
  }

  refreshWorkflowHotspotFlowRow(hotspotId, oldId = null) {
    const targetId = String(hotspotId)
    const previousId = oldId ? String(oldId) : null

    this.removeWorkflowHotspotFlowDuplicates(targetId, previousId)

    const hs = this.hotspots.find((h) => String(h.id) === targetId)
    if (!hs) return

    const interactionsMap = this.interactionsByHotspotId
    const interactions =
      interactionsMap && typeof interactionsMap.get === "function"
        ? (interactionsMap.get(hs.id) || [])
        : []

    const activeInteractions = interactions
      .filter((it) => it && it.is_active !== false)
      .sort((a, b) => (a.position || 0) - (b.position || 0))

    const detailsText = activeInteractions.length > 0
      ? activeInteractions.map((it) => `#${it.position} ${it.kind}`).join(" · ")
      : ""

    const availableRoot = this.element.querySelector("[data-workflow-available-list]")
    if (availableRoot) {
      const existingAvailable = availableRoot.querySelector(
        `[data-workflow-item-group][data-workflow-kind="hotspot_flow"][data-hotspot-id="${targetId}"]`
      )

      if (existingAvailable) {
        const nameCell = existingAvailable.querySelector("[data-workflow-object]")
        const destCell = existingAvailable.querySelector("[data-workflow-destination]")
        const detailsCell = existingAvailable.querySelector("[data-workflow-details]")

        if (nameCell) nameCell.textContent = hs.name || ""

        if (destCell) {
          destCell.innerHTML = `<span class="text-gray-700">${activeInteractions.length} interazioni</span>`
        }

        if (detailsCell) {
          detailsCell.innerHTML = `
            Apertura hotspot e completamento di tutte le interazioni collegate.
            ${detailsText ? `<div class="mt-1 text-xs text-gray-500">${detailsText}</div>` : ""}
          `
        }
      }
    }

    const usedRoot = this.element.querySelector("[data-workflow-used-list]")
    if (usedRoot) {
      const existingUsed = usedRoot.querySelector(
        `[data-workflow-item-group][data-workflow-kind="hotspot_flow"][data-hotspot-id="${targetId}"]`
      )

      if (existingUsed) {
        const nameCell = existingUsed.querySelector("[data-workflow-object]")
        const destCell = existingUsed.querySelector("[data-workflow-destination]")

        if (nameCell) nameCell.textContent = hs.name || ""
        if (destCell) destCell.textContent = `${activeInteractions.length} interazioni`
      }
    }
  }

  removeWorkflowHotspotFlowDuplicates(hotspotId, oldId = null) {
    const ids = [String(hotspotId)]
    if (oldId && String(oldId) !== String(hotspotId)) {
      ids.push(String(oldId))
    }

    const availableRoot = this.element.querySelector("[data-workflow-available-list]")
    const usedRoot = this.element.querySelector("[data-workflow-used-list]")

    ids.forEach((id) => {
      if (availableRoot) {
        const availableNodes = Array.from(
          availableRoot.querySelectorAll(
            `[data-workflow-item-group][data-workflow-kind="hotspot_flow"][data-hotspot-id="${id}"]`
          )
        )

        availableNodes.forEach((node, index) => {
          if (index > 0) node.remove()
        })
      }

      if (usedRoot) {
        const usedNodes = Array.from(
          usedRoot.querySelectorAll(
            `[data-workflow-item-group][data-workflow-kind="hotspot_flow"][data-hotspot-id="${id}"]`
          )
        )

        usedNodes.forEach((node, index) => {
          if (index > 0) node.remove()
        })
      }
    })
  }

  showWorkflowFeedback(message, type = "success") {
    if (!this.hasWorkflowFeedbackTarget || !this.hasWorkflowFeedbackInnerTarget || !this.hasWorkflowFeedbackTextTarget) return

    clearTimeout(this._workflowFeedbackTimer)

    this.workflowFeedbackTextTarget.textContent = message
    this.workflowFeedbackInnerTarget.classList.remove("is-success", "is-error", "is-warning")
    this.workflowFeedbackInnerTarget.classList.add(
      type === "error" ? "is-error" : type === "warning" ? "is-warning" : "is-success"
    )

    this.workflowFeedbackTarget.classList.remove("hidden")
    this.workflowFeedbackTarget.classList.add("is-visible")

    this._workflowFeedbackTimer = setTimeout(() => {
      if (!this.hasWorkflowFeedbackTarget) return
      this.workflowFeedbackTarget.classList.remove("is-visible")

      setTimeout(() => {
        if (this.hasWorkflowFeedbackTarget) this.workflowFeedbackTarget.classList.add("hidden")
      }, 180)
    }, 2200)
  }
}